import { useState, useEffect } from 'react';
import { TrendingDown, TrendingUp, DateRange, AttachMoney, People, Assessment } from '@mui/icons-material';

const WeeklyStats = () => {
  const [timeframe, setTimeframe] = useState('week');
  const [stats, setStats] = useState({
    week: {
      revenue: 89000,
      customers: 45,
      engagement: 78,
      revenueChange: -4.3,
      customersChange: 12.5,
      engagementChange: 8.2
    },
    month: {
      revenue: 356000,
      customers: 187,
      engagement: 82,
      revenueChange: 2.8,
      customersChange: 5.1,
      engagementChange: 3.4
    },
    quarter: {
      revenue: 1068000,
      customers: 502,
      engagement: 85,
      revenueChange: 5.2,
      customersChange: 8.7,
      engagementChange: 6.1
    }
  });

  const [customerMetrics, setCustomerMetrics] = useState({
    totalCustomers: 502,
    activeCustomers: 387,
    newCustomers: 23,
    churnedCustomers: 8
  });

  useEffect(() => {
    // Simulate data based on CSV structure
    // In a real app, this would be calculated from the actual CSV data
    setCustomerMetrics({
      totalCustomers: 502,
      activeCustomers: 387,
      newCustomers: 23,
      churnedCustomers: 8
    });
  }, []);

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
  };

  const currentStats = stats[timeframe];

  const getMetricIcon = (metric) => {
    switch (metric) {
      case 'revenue':
        return <AttachMoney className="text-[#B4E50D]" />;
      case 'customers':
        return <People className="text-[#B4E50D]" />;
      case 'engagement':
        return <Assessment className="text-[#B4E50D]" />;
      default:
        return <TrendingUp className="text-[#B4E50D]" />;
    }
  };

  const getChangeColor = (change) => {
    return change >= 0 ? 'text-[#B4E50D]' : 'text-[#FF6B6B]';
  };

  const getChangeIcon = (change) => {
    return change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />;
  };

  return (
    <div className="bg-[#1A1A1A] rounded-xl p-6 shadow-xl border border-[#2A2A2A] hover:border-[#B4E50D] transition-all duration-300">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-[#FFFFFF]">Performance Stats</h2>
        <div className="flex bg-[#2A2A2A] rounded-xl p-1">
          {['week', 'month', 'quarter'].map((period) => (
            <button
              key={period}
              onClick={() => handleTimeframeChange(period)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                timeframe === period 
                  ? 'bg-[#B4E50D] text-[#0A0A0A] shadow-lg' 
                  : 'text-[#B0B0B0] hover:text-[#FFFFFF] hover:bg-[#3A3A3A]'
              }`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Main Revenue Metric */}
      <div className="mb-6 p-4 bg-gradient-to-r from-[#2A2A2A] to-[#3A3A3A] rounded-xl border border-[#3A3A3A]">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-[#B0B0B0] mb-1">Total Revenue</div>
            <div className="text-3xl font-bold text-[#FFFFFF]">
              ${currentStats.revenue.toLocaleString()}
            </div>
          </div>
          <div className="text-right">
            <div className={`flex items-center text-sm font-medium ${getChangeColor(currentStats.revenueChange)}`}>
              {getChangeIcon(currentStats.revenueChange)}
              <span className="ml-1">{Math.abs(currentStats.revenueChange)}%</span>
            </div>
            <div className="text-xs text-[#B0B0B0]">vs last {timeframe}</div>
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-3 bg-[#2A2A2A] rounded-xl border border-[#2A2A2A]">
          <div className="flex items-center space-x-2 mb-2">
            {getMetricIcon('customers')}
            <span className="text-sm text-[#B0B0B0]">Customers</span>
          </div>
          <div className="text-lg font-bold text-[#FFFFFF]">{currentStats.customers}</div>
          <div className={`flex items-center text-xs ${getChangeColor(currentStats.customersChange)}`}>
            {getChangeIcon(currentStats.customersChange)}
            <span className="ml-1">{Math.abs(currentStats.customersChange)}%</span>
          </div>
        </div>

        <div className="p-3 bg-[#2A2A2A] rounded-xl border border-[#2A2A2A]">
          <div className="flex items-center space-x-2 mb-2">
            {getMetricIcon('engagement')}
            <span className="text-sm text-[#B0B0B0]">Engagement</span>
          </div>
          <div className="text-lg font-bold text-[#FFFFFF]">{currentStats.engagement}%</div>
          <div className={`flex items-center text-xs ${getChangeColor(currentStats.engagementChange)}`}>
            {getChangeIcon(currentStats.engagementChange)}
            <span className="ml-1">{Math.abs(currentStats.engagementChange)}%</span>
          </div>
        </div>
      </div>

      {/* Customer Overview */}
      <div className="mb-6 p-4 bg-[#2A2A2A] rounded-xl border border-[#3A3A3A]">
        <h3 className="text-sm font-medium text-[#FFFFFF] mb-3">Customer Overview</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-xl font-bold text-[#B4E50D]">{customerMetrics.totalCustomers}</div>
            <div className="text-xs text-[#B0B0B0]">Total</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-[#B4E50D]">{customerMetrics.activeCustomers}</div>
            <div className="text-xs text-[#B0B0B0]">Active</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-[#B4E50D]">+{customerMetrics.newCustomers}</div>
            <div className="text-xs text-[#B0B0B0]">New</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-[#FF6B6B]">-{customerMetrics.churnedCustomers}</div>
            <div className="text-xs text-[#B0B0B0]">Churned</div>
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-[#2A2A2A]">
        <div className="flex items-center text-sm text-[#B0B0B0]">
          <DateRange className="w-4 h-4 mr-2" />
          <span>Last updated: {new Date().toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

export default WeeklyStats;